import { Injectable } from '@angular/core';
import Decimal from 'decimal.js';
import { roundCorrectly } from '../../../general/utils';
import { Estado } from '../../../model/estado';
import { IAliquotaICMS } from '../../../model/IAliquotaICMS';
import { MVAEstado } from '../../../model/MVAEstado';
import { ClassificacoesFiscaisService } from '../../../services/classificacoes-fiscais.service';
import { ItensService } from '../../../services/itens.service';

@Injectable()
export class EmissaoNfeService {
  constructor(
    private itensService: ItensService,
    private classificService: ClassificacoesFiscaisService,
  ) { }

  async getItem(produtoId) {
    const query =
      // eslint-disable-next-line max-len
      `(${produtoId})?$select=id, nome, estoqueMinimo, Peso, PesoBruto, classificacaoFiscalId, codigoCestId, origemMercadoria, descricaoComplementar, almoxarifadoPadraoId, precoSaida, custoCompra, ipiSituacaoTributaria, pisSituacaoTributaria, cofinsSituacaoTributaria, aliquotaicmsid, aliquotaIpi, pis, cofins, tipoItemID, tipoBaseCalculoPISCOFINS, percentualDif_ProEmprego, percentualDiferimento, percDiferimentoIBSCBS&$expand=unidadeMedida($select=unidade), aliquotaICMS($select=aliquotaICMS), itensFornecedores($select=referenciaItem; $expand=fornecedor($select=cnpj))`;
    return this.itensService.getByOData(query).toPromise();
  }

  // Dar preferência a função calcularICMS2, que recebe alíquota e % redução diretamente.
  calcularICMS(
    aliquotasIcms: IAliquotaICMS[],
    aliquotaCreditoSimplesNacional: number,
    clienteDentroDoEstado: boolean,
    aliquotaInterestadual: number, // entre Estado do emissor e cliente
    aliquotaImportacao: number, // entre Estado do emissor e cliente
    codigoSTICMS: string,
    origemMercadoria: any,
    quantidade: number,
    preco: number,
    valorDesconto: number,
    valorFrete: any,
    valorSeguro: any,
    aliquotaIcmsID: number,
    valorIPI: number,
    somaIpi: boolean,
    reducaoInterestadual?: number,
    proEmprego?: boolean,
    destacarICMSProEmprego?: boolean,
    percentuaDiferimentoProEmprego?: number,
  ): CalculosICMS {
    const impostos = {
      baseCalculoICMS: 0,
      valorCreditoICMS: 0,
      modBcIcms: 3,
      modBcStIcms: 4,
      aliquotaICMSID: null,
      aliquotaICMS: 0,
      valorICMS: 0,
      reducaoBaseCalculoICMS: 0,
    };

    if (proEmprego && !destacarICMSProEmprego) {
      return impostos;
    }

    let baseCalculoICMS = (quantidade * preco) - valorDesconto + valorFrete + valorSeguro;

    switch (codigoSTICMS) {
      case '101':
      case '201':
        impostos.baseCalculoICMS = baseCalculoICMS; // Verificar pois no CalcularICMS2 removi essa linha. No XML não existe BC ICMS para Simples.
        impostos.valorCreditoICMS = baseCalculoICMS * aliquotaCreditoSimplesNacional / 100;

        if (aliquotasIcms && aliquotaIcmsID) {
          impostos.aliquotaICMSID = aliquotaIcmsID;
        }

        break;
      case '00':
      case '10':
      case '20': {
        impostos.modBcStIcms = 5;
        baseCalculoICMS = baseCalculoICMS && somaIpi
          ? baseCalculoICMS + valorIPI
          : baseCalculoICMS;

        let aliquotaICMS = 0;
        let percentualRedutorICMS = 0;
        let aliquotaProduto = null;

        if (aliquotasIcms && aliquotaIcmsID) {
          impostos.aliquotaICMSID = aliquotaIcmsID;
          aliquotaProduto = aliquotasIcms.find(x => x.id === aliquotaIcmsID);
        }

        if (aliquotaProduto && codigoSTICMS === '20') {
          percentualRedutorICMS = reducaoInterestadual || aliquotaProduto.percentualRedutorICMS || 0;
          baseCalculoICMS -= baseCalculoICMS * percentualRedutorICMS / 100;
        }

        if (clienteDentroDoEstado) {
          aliquotaICMS = aliquotaProduto ? aliquotaProduto.aliquotaICMS : 0;
        } else if ([0, 4, 5, 6, 7].includes(origemMercadoria || 0)) {
          aliquotaICMS = aliquotaInterestadual;
        } else if ([1, 2, 3, 8].includes(origemMercadoria || 0)) {
          aliquotaICMS = aliquotaImportacao;
        }

        impostos.aliquotaICMS = aliquotaICMS;
        impostos.baseCalculoICMS = baseCalculoICMS;
        impostos.valorICMS = baseCalculoICMS * aliquotaICMS / 100;
        impostos.reducaoBaseCalculoICMS = percentualRedutorICMS;

        break;
      }
      case '51': {
        impostos.modBcStIcms = 5;
        baseCalculoICMS = baseCalculoICMS && somaIpi
          ? baseCalculoICMS + valorIPI
          : baseCalculoICMS;
        let aliquotaICMS = 0;
        let percentualRedutorICMS = 0;
        let aliquotaProduto = null;

        if (aliquotasIcms && aliquotaIcmsID) {
          impostos.aliquotaICMSID = aliquotaIcmsID;
          aliquotaProduto = aliquotasIcms.find(x => x.id === aliquotaIcmsID);
        }

        if (clienteDentroDoEstado) {
          aliquotaICMS = aliquotaProduto ? aliquotaProduto.aliquotaICMS : 0;
        } else if ([0, 4, 5, 6, 7].includes(origemMercadoria || 0)) {
          aliquotaICMS = aliquotaInterestadual;
        } else if ([1, 2, 3, 8].includes(origemMercadoria || 0)) {
          aliquotaICMS = aliquotaImportacao;
        }

        const valorICMSOperacao = baseCalculoICMS * aliquotaICMS / 100;
        impostos.aliquotaICMS = aliquotaICMS;
        impostos.valorICMS = valorICMSOperacao;
        impostos.baseCalculoICMS = baseCalculoICMS;
        impostos.reducaoBaseCalculoICMS = percentualRedutorICMS;

        if (proEmprego && percentuaDiferimentoProEmprego > 0) {
          const valorICMSDiferido = valorICMSOperacao * (percentuaDiferimentoProEmprego / 100);
          impostos.valorICMS = valorICMSOperacao - valorICMSDiferido;
        } else {
          impostos.valorICMS = valorICMSOperacao;
        }
      }
    }

    return impostos;
  }

  calcularICMS2(
    aliquotaCreditoSimplesNacional: number,
    clienteDentroDoEstado: boolean,
    aliquotaDentroEstado: number,
    aliquotaInterestadual: number, // entre Estado do emissor e cliente
    aliquotaImportacao: number, // entre Estado do emissor e cliente
    codigoSTICMS: string,
    origemMercadoria: number,
    quantidade: number,
    preco: number,
    valorDesconto: number,
    valorFrete: number,
    valorSeguro: number,
    valorIPI: number,
    somaIpi: boolean,
    percentualRedutorICMS: number,
    aplicarDescontoBase: boolean,
    valorOutras: number,
    aplicarOutrasBaseIcms: boolean,
  ): CalculosICMS2 {
    aliquotaCreditoSimplesNacional = aliquotaCreditoSimplesNacional || 0;
    aliquotaDentroEstado = aliquotaDentroEstado || 0;
    aliquotaInterestadual = aliquotaInterestadual || 0;
    aliquotaImportacao = aliquotaImportacao || 0;
    origemMercadoria = origemMercadoria || 0; // Se tiver vazio considera como mercadoria Nacional
    quantidade = quantidade || 0;
    preco = preco || 0;
    valorDesconto = valorDesconto || 0;
    valorFrete = valorFrete || 0;
    valorSeguro = valorSeguro || 0;
    valorIPI = valorIPI || 0;
    percentualRedutorICMS = percentualRedutorICMS || 0;
    valorOutras = valorOutras || 0;

    const impostos: CalculosICMS2 = {
      baseCalculoICMS: 0,
      valorCreditoICMS: 0,
      modBcIcms: 3,
      modBcStIcms: 4,
      aliquotaICMS: 0,
      valorICMS: 0,
      reducaoBaseCalculoICMS: 0,
    };

    let baseCalculoICMS = (quantidade * preco) + valorFrete + valorSeguro;
    if (aplicarDescontoBase) {
      baseCalculoICMS -= valorDesconto;
    }
    if (aplicarOutrasBaseIcms) {
      baseCalculoICMS += valorOutras;
    }

    switch (codigoSTICMS) {
      case '101':
      case '201':
        impostos.valorCreditoICMS = roundCorrectly(baseCalculoICMS * aliquotaCreditoSimplesNacional / 100, 2).toNumber();
        break;
      case '00':
      case '10':
      case '20': {
        impostos.modBcStIcms = 5;
        baseCalculoICMS = baseCalculoICMS + (somaIpi ? valorIPI : 0);

        if (codigoSTICMS === '20') {
          baseCalculoICMS -= baseCalculoICMS * (percentualRedutorICMS || 0) / 100;
        }

        let aliquotaICMS = 0;
        if (clienteDentroDoEstado) {
          aliquotaICMS = aliquotaDentroEstado;
        } else if ([0, 4, 5, 6, 7].includes(origemMercadoria)) {
          aliquotaICMS = aliquotaInterestadual;
        } else if ([1, 2, 3, 8].includes(origemMercadoria)) {
          aliquotaICMS = aliquotaImportacao;
        }

        impostos.aliquotaICMS = aliquotaICMS;
        impostos.baseCalculoICMS = roundCorrectly(baseCalculoICMS, 2).toNumber();
        impostos.valorICMS = roundCorrectly(baseCalculoICMS * aliquotaICMS / 100, 2).toNumber();
        impostos.reducaoBaseCalculoICMS = percentualRedutorICMS;

        break;
      }
    }

    return impostos;
  }

  calcularIPI(cst: string, valorItem: number, aliquota: number, desconto: number, frete: number, aplicarDescontoBase: boolean, valorII: number, somarII: boolean): CalculosIPI {
    const impostos = {
      valorIPI: 0,
      baseCalculoIPI: 0,
    };

    valorII = valorII || 0;

    if (['01', '02', '03', '04', '05', '51', '52', '53', '54', '55'].includes(cst)) {
      return impostos;
    }

    if ((aliquota || 0) > 0) {
      let valorTotal = new Decimal(valorItem || 0).plus(frete || 0);
      if (aplicarDescontoBase) {
        valorTotal = valorTotal.minus(desconto || 0);
      }

      impostos.baseCalculoIPI = roundCorrectly(valorTotal, 2).toNumber();
      if (somarII) {
        impostos.baseCalculoIPI = roundCorrectly(impostos.baseCalculoIPI + valorII, 2).toNumber();
      }
      if ((impostos.baseCalculoIPI || 0) > 0) {
        impostos.valorIPI = roundCorrectly(new Decimal(impostos.baseCalculoIPI || 0).mul(aliquota || 0).div(100), 2).toNumber();
      }
    } else {
      impostos.baseCalculoIPI = 0;
    }

    return impostos;
  }

  calcularPIS(cst: string, quantidade: number, preco: number, aliquota: number, desconto: number,
    aplicarDescontoBase: boolean, valorICMS = 0, abaterICMSBasePISCOFINS = false, custoCompra = 0, baseSobreLucro = false
  ): CalculosPIS {
    const impostos = {
      valorPIS: 0,
      baseCalculoPIS: 0,
    };

    if (['04', '05', '06', '07', '08', '09', '99'].includes(cst)
      || aliquota <= 0) { // TODO 99 na verdade teria valor... acho que temos que considerar o CRT também.
      return impostos;
    }

    let valorTotal = new Decimal(quantidade || 0).mul(preco || 0);
    if (aplicarDescontoBase) {
      valorTotal = valorTotal.minus(desconto || 0);
    }

    impostos.baseCalculoPIS = baseSobreLucro
      ? roundCorrectly(valorTotal.minus(custoCompra), 2).toNumber()
      : roundCorrectly(valorTotal, 2).toNumber();

    if (abaterICMSBasePISCOFINS) {
      impostos.baseCalculoPIS -= valorICMS;
      if (impostos.baseCalculoPIS < 0) {
        impostos.baseCalculoPIS = 0;
      }
    }
    if (impostos.baseCalculoPIS > 0) {
      impostos.valorPIS = roundCorrectly(new Decimal(impostos.baseCalculoPIS || 0).mul(aliquota || 0).div(100), 2).toNumber();
    }

    return impostos;
  }

  calcularCOFINS(cst: string, quantidade: number, preco: number, aliquota: number, desconto: number,
    aplicarDescontoBase: boolean, valorICMS = 0, abaterICMSBasePISCOFINS = false, custoCompra = 0, baseSobreLucro = false
  ): CalculosCOFINS {
    const impostos = {
      valorCOFINS: 0,
      baseCalculoCOFINS: 0,
    };

    if (['04', '05', '06', '07', '08', '09', '99'].includes(cst)
      || aliquota <= 0) { // TODO 99 na verdade teria valor... acho que temos que considerar o CRT também.
      return impostos;
    }

    let valorTotal = new Decimal(quantidade || 0).mul(preco || 0);
    if (aplicarDescontoBase) {
      valorTotal = valorTotal.minus(desconto || 0);
    }

    impostos.baseCalculoCOFINS = baseSobreLucro
      ? roundCorrectly(valorTotal.minus(custoCompra), 2).toNumber()
      : roundCorrectly(valorTotal, 2).toNumber();

    if (abaterICMSBasePISCOFINS) {
      impostos.baseCalculoCOFINS -= valorICMS;
      if (impostos.baseCalculoCOFINS < 0) {
        impostos.baseCalculoCOFINS = 0;
      }
    }
    if (impostos.baseCalculoCOFINS > 0) {
      impostos.valorCOFINS = roundCorrectly(new Decimal(impostos.baseCalculoCOFINS || 0).mul(aliquota || 0).div(100), 2).toNumber();
    }

    return impostos;
  }

  async getCodigosCest(classFiscalId: number) {
    const res = await this.classificService.getById(classFiscalId).toPromise();
    if (!res) {
      return [];
    }

    const cests = await this.classificService.getCest(res.classificacaoFiscal).toPromise();
    return cests;
  }

  permiteDifal(crt: number, clienteDentroDoEstado: boolean, clienteFinal: boolean,
    inscricaoEstadual: string, stIcms: string, tipoNfeId: number, estadoDestinoPermiteClienteIsento: boolean,
    presencaComprador: number) {
    // Deixar essa função de acordo com o backend: NotasFiscaisItens.cs
    const notaSaida = ![15, 16].includes(tipoNfeId);
    const indicadorIE = this.calcularIndicadorIE(inscricaoEstadual, clienteDentroDoEstado, clienteFinal, estadoDestinoPermiteClienteIsento);

    return [2, 3].includes(crt)
      && notaSaida
      && !clienteDentroDoEstado
      && clienteFinal
      && indicadorIE === 9
      && ['00', '20'].includes(stIcms)
      && presencaComprador !== 1;
  }

  calcularIndicadorIE(inscricaoEstadual: string, clienteDentroDoEstado: boolean, clienteFinal: boolean, estadoDestinoPermiteClienteIsento: boolean) {
    // Deixar essa função de acordo com o backend: NotasFiscais.cs

    // Regras para determinar o indIEDest, decidido pelo Edson. Qualquer dúvida ver com ele:
    // - Se tem IE, venda para dentro do estado e não é Cliente Final, indIEDest = 1
    // - Se tem IE, venda para dentro do estado e é Cliente Final, indIEDest = 1
    // - Se tem IE, venda para fora do estado e Não é Cliente Final, indIEDest = 1
    // - Se tem IE, venda para fora do estado e é Cliente Final, indIEDest = 9
    // - Se não tem IE, venda dentro do estado e estado destino permite inscrição isenta, indIEDest = 2
    // - Se não tem IE, venda dentro do estado e estado destino não permite inscrição isenta, indIEDest = 9
    // - Se não tem IE e venda para fora do estado, indIEDest = 9

    const temIE = inscricaoEstadual && inscricaoEstadual.length > 0 && inscricaoEstadual.toUpperCase() !== 'ISENTO';

    let indIEDest = 9;
    if (temIE && clienteDentroDoEstado && !clienteFinal) {
      indIEDest = 1;
    } else if (temIE && clienteDentroDoEstado && clienteFinal) {
      indIEDest = 1;
    } else if (temIE && !clienteDentroDoEstado && !clienteFinal) {
      indIEDest = 1;
    } else if (temIE && !clienteDentroDoEstado && clienteFinal) {
      indIEDest = 9;
    } else if (!temIE && clienteDentroDoEstado && estadoDestinoPermiteClienteIsento) {
      indIEDest = 2;
    } else if (!temIE && clienteDentroDoEstado && !estadoDestinoPermiteClienteIsento) {
      indIEDest = 9;
    } else if (!temIE && !clienteDentroDoEstado) {
      indIEDest = 9;
    }

    return indIEDest;
  }

  calcularDifal(
    crt: number,
    clienteDentroDoEstado: boolean,
    clienteFinal: boolean,
    inscricaoEstadual: string,
    stIcms: string,
    tipoNfeId: number,
    baseCalculoICMS: number,
    aliquotaInterEstadual: number, // entre Estado do emissor e cliente
    aliquotaImportacao: number, // entre Estado do emissor e cliente
    aliquotaInterna: number, // Estado do cliente
    origemMercadoria: number,
    estadoDestinoPermiteClienteIsento: boolean,
    aliquotaIcmsProduto: number,
    presencaComprador: number,
  ) {
    // Deixar essa função de acordo com o backend: NotasFiscaisItens.cs
    // Obs: A CST 20 é um caso especial do Edson, ver função do backend. No frontend é só ocultar a aba DIFAL.

    if (!this.permiteDifal(crt, clienteDentroDoEstado, clienteFinal, inscricaoEstadual, stIcms,
      tipoNfeId, estadoDestinoPermiteClienteIsento, presencaComprador)) {
      return {
        difalBCUFDest: 0,
        difalAliqInter: 0,
        difalAliqIntra: 0,
        difalValor: 0,
        difalValorIcmsInter: 0,
      };
    }

    const bcIcms = roundCorrectly(new Decimal(baseCalculoICMS || 0), 2);
    const aliqInter = [0, 4, 5, 6, 7].includes(origemMercadoria || 0)
      ? new Decimal(aliquotaInterEstadual || 0)
      : new Decimal(aliquotaImportacao || 0);
    const aliqIntra = new Decimal(aliquotaInterna || 0);

    if (aliqInter.toNumber() === aliquotaIcmsProduto) {
      // WEB-3149 - Se a alíquota ICMS do produto for igual a alíquota da venda, enviar DIFAL zerado. É obrigatório mandar as tags.
      return {
        difalBCUFDest: 0,
        difalAliqInter: aliqInter,
        difalAliqIntra: aliqInter,
        difalValor: 0,
        difalValorIcmsInter: 0,
      };
    }

    // Implementado apenas o cálculo por fora, pois o cálculo por dentro é só para destinatário contribuinte do ICMS.
    const icmsOrigem = roundCorrectly(bcIcms.mul(aliqIntra).div(100), 2);
    const icmsDestino = roundCorrectly(bcIcms.mul(aliqInter).div(100), 2);
    const valorDifal = icmsOrigem.minus(icmsDestino);
    return {
      difalBCUFDest: roundCorrectly(bcIcms, 2).toNumber(),
      difalAliqInter: roundCorrectly(aliqInter, 4).toNumber(),
      difalAliqIntra: roundCorrectly(aliqIntra, 4).toNumber(),
      difalValor: roundCorrectly(valorDifal, 2).toNumber(),
      difalValorIcmsInter: roundCorrectly(icmsDestino, 2).toNumber(),
    };
  }

  calcularICMSST(
    preco: number,
    quantidade: number,
    valorDesconto: number,
    origemMercadoria: number,
    operacaoOutrasSaidas: boolean,
    nfeDevolucao: boolean,
    codigoSTICMS: string,
    mvaPorEstado: MVAEstado,
    aliquotaICMS: IAliquotaICMS,
    custoCompra: number,
    estadoEmpresa: Estado,
    estadoCliente: Estado,
    clienteSimples: boolean,
    valorIPI: number,
    valorFrete: number,
    valorSeguro: number,
    valorOutras: number,
    aliquotaInterEstadual: number, // entre Estado do emissor e cliente
    aliquotaImportacao: number, // entre Estado do emissor e cliente
  ): CalculosICMSST {
    const impostos = {
      baseCalculoICMSSTRetido: 0,
      aliquotaICMSSTRetido: 0,
      valorICMSSTRetido: 0,
      valorICMSSubstituto: 0,
      mva: 0,
      baseCalculoICMSST: 0,
      aliquotaICMSST: 0,
      valorICMSST: 0,
      redutorMVA: 0,
    };

    if (operacaoOutrasSaidas || !mvaPorEstado || nfeDevolucao) {
      return impostos;
    }

    const calcular = ['10', '30', '60', '70', '201', '202', '203'].includes(codigoSTICMS);

    if (codigoSTICMS === '60') {
      // Deixar essa rotina de acordo com o backend: NotasFiscaisItens.cs função CalculaValorIcmsSt
      const precoCustoUnitario = new Decimal(custoCompra || 0);
      if (!mvaPorEstado || precoCustoUnitario.toNumber() <= 0) {
        return impostos;
      }

      const precoCusto = precoCustoUnitario.mul(quantidade || 0);
      const aliquotaEntrada = aliquotaICMS ? aliquotaICMS.aliquotaICMS : 0;
      const mva = new Decimal(mvaPorEstado.mva || 0);

      const icmsEntrada = roundCorrectly(precoCusto
        .plus(valorIPI || 0)
        .plus(valorFrete || 0)
        .plus(valorSeguro || 0)
        .plus(valorOutras || 0)
        .mul(aliquotaEntrada).div(100), 2);
      const baseIcmsSt = roundCorrectly(precoCusto
        .plus(valorIPI || 0)
        .plus(valorFrete || 0)
        .plus(valorSeguro || 0)
        .plus(valorOutras || 0)
        .mul(new Decimal(100).plus(mva).div(100)), 2);

      const aliquotaInternaEmitente = new Decimal(estadoEmpresa.aliquotaInterna || 0);
      const icmsSaida = roundCorrectly(baseIcmsSt.mul(aliquotaInternaEmitente).div(100), 2);
      const icms60 = icmsSaida.minus(icmsEntrada);

      impostos.baseCalculoICMSSTRetido = baseIcmsSt.toNumber();
      impostos.aliquotaICMSSTRetido = estadoCliente.aliquotaInterna;
      impostos.valorICMSSTRetido = icms60.toNumber();
      impostos.valorICMSSubstituto = icmsEntrada.toNumber();
      impostos.mva = mvaPorEstado.mva;
    } else if (calcular) {
      origemMercadoria = origemMercadoria || 0;
      let aliquotaSimulada = 0;

      if (estadoEmpresa.id === estadoCliente.id) {
        aliquotaSimulada = aliquotaICMS ? aliquotaICMS.aliquotaICMS : 0;
      } else {
        if ([0, 4, 5, 6, 7].includes(origemMercadoria)) {
          aliquotaSimulada = aliquotaInterEstadual;
        }

        if ([1, 2, 3, 8].includes(origemMercadoria)) {
          aliquotaSimulada = aliquotaImportacao;
        }
      }

      if (aliquotaSimulada) {
        if (!mvaPorEstado) {
          return impostos;
        }

        const totalItem = new Decimal(preco || 0).mul(quantidade || 0).minus(valorDesconto || 0).toNumber();
        const valorICMSSTSimulado = (totalItem + valorIPI) * aliquotaSimulada / 100;
        let percentualMargemAdicionalSTICMS = 0;

        if (clienteSimples) {
          percentualMargemAdicionalSTICMS = mvaPorEstado.mva * ((100 - mvaPorEstado.redutor) / 100);
        } else if (mvaPorEstado.mvaAjustada) {
          percentualMargemAdicionalSTICMS =
            ((((1 + (mvaPorEstado.mva / 100)) *
              (1 - (aliquotaInterEstadual / 100))) /
              (1 - (estadoCliente.aliquotaInterna / 100))) - 1) * 100;
        } else {
          percentualMargemAdicionalSTICMS = mvaPorEstado.mva;
        }

        const frete = valorFrete || 0;
        const baseCalculoICMSST = (totalItem + valorIPI + frete) * (1 + (percentualMargemAdicionalSTICMS / 100));
        const valorICMSST = ((baseCalculoICMSST * estadoCliente.aliquotaInterna) / 100) - valorICMSSTSimulado;

        impostos.baseCalculoICMSST = baseCalculoICMSST;
        impostos.aliquotaICMSST = estadoCliente.aliquotaInterna;
        impostos.valorICMSST = valorICMSST;
        impostos.mva = mvaPorEstado.mva;
        impostos.redutorMVA = mvaPorEstado.redutor || 0;
      } else {
        impostos.baseCalculoICMSST = 0;
        impostos.aliquotaICMSST = 0;
        impostos.valorICMSST = 0;
        impostos.mva = 0;
        impostos.redutorMVA = 0;
      }
    }

    return impostos;
  }

  cstPermiteIcmsProprio(cst: string) {
    return ['00', '10', '20', '51', '70', '90', '900'].includes(cst);
  }

  cstPermiteReducaoBaseIcms(cst: string) {
    return ['20', '51', '70', '90', '900'].includes(cst);
  }

  cstPermiteCreditoIcms(cst: string) {
    return ['101', '201', '900'].includes(cst);
  }

  cstPermiteIcmsSt(cst: string) {
    return ['10', '30', '60', '70', '90', '201', '202', '203', '500', '900'].includes(cst);
  }

  cstPermiteValorPis(cst: string) {
    return cst && !['04', '05', '06', '07', '08', '09'].includes(cst);
  }

  cstPermiteValorCofins(cst: string) {
    return cst && !['04', '05', '06', '07', '08', '09'].includes(cst);
  }

  cstPermiteValorIpi(cst: string) {
    return ['00', '49', '50', '99'].includes(cst);
  }

  calcularBaseIBSCBS(cst: string, preco: number, quantidade: number, desconto: number,
    icms: number, pis: number, cofins: number, frete: number, seguro: number, outrasDespesas: number,
    ii: number, is: number
  ): number | null {
    if (['000', '200', '510', '515', '550'].includes(cst)) {
      const valorProduto = roundCorrectly(new Decimal(preco || 0).mul(quantidade || 0), 2);
      const base = roundCorrectly(valorProduto.minus(desconto || 0).minus(icms || 0).minus(pis || 0).minus(cofins || 0)
        .plus(frete || 0).plus(seguro || 0).plus(outrasDespesas || 0).plus(ii || 0).plus(is || 0), 2);

      return base.toNumber() >= 0 ? base.toNumber() : 0;
    }

    return null;
  }

  calcularValorIBSCBS(cst: string, base: number, aliquota: number, percReducao: number, percDiferimento: number)
    : {
      aliquotaEfetiva?: number,
      valor?: number,
      valorDiferimento?: number,
    } {
    if (cst === '200') {
      // Cálculo na descrição da rejeição 1035
      const aliquotaEfetiva = roundCorrectly(new Decimal(aliquota || 0).mul(new Decimal(1).minus(new Decimal(percReducao || 0).div(100))), 4);
      const valor = roundCorrectly(new Decimal(base || 0).mul(aliquotaEfetiva).div(100), 2);

      return {
        aliquotaEfetiva: aliquotaEfetiva.toNumber(),
        valor: valor.toNumber(),
      };
    } else if (cst === '510') {
      // Cálculo na descrição da rejeição 1031, 1041
      const valorDiferimento = roundCorrectly(new Decimal(base || 0).mul(new Decimal(aliquota || 0).div(100)).mul(new Decimal(percDiferimento || 0).div(100)), 2);
      const valor = roundCorrectly(new Decimal(base || 0).mul(aliquota || 0).div(100).minus(valorDiferimento), 2);

      return {
        valor: valor.toNumber(),
        valorDiferimento: valorDiferimento.toNumber(),
      };
    } else if (cst === '515') {
      const aliquotaEfetiva = roundCorrectly(new Decimal(aliquota || 0).mul(new Decimal(1).minus(new Decimal(percReducao || 0).div(100))), 4);
      const valorDiferimento = roundCorrectly(new Decimal(base || 0).mul(new Decimal(aliquotaEfetiva || 0).div(100)).mul(new Decimal(percDiferimento || 0).div(100)), 2);
      const valor = roundCorrectly(new Decimal(base || 0).mul(aliquotaEfetiva || 0).div(100).minus(valorDiferimento), 2);

      return {
        aliquotaEfetiva: aliquotaEfetiva.toNumber(),
        valor: valor.toNumber(),
        valorDiferimento: valorDiferimento.toNumber(),
      };
    } else {
      const valor = roundCorrectly(new Decimal(base || 0).mul(aliquota || 0).div(100), 2);

      return {
        valor: valor.toNumber(),
      };
    }
  }

  possuiAliquotaIBSCBS(cst: string) {
    return ['000', '200', '510', '515', '550'].includes(cst);
  }

  calcularAliquotaIBSMun(cst: string): number | null {
    if (this.possuiAliquotaIBSCBS(cst)) {
      return 0; // Em 2025/2026
    }

    return null;
  }

  calcularAliquotaIBSUF(cst: string): number | null {
    if (this.possuiAliquotaIBSCBS(cst)) {
      return 0.1; // Em 2025/2026
    }

    return null;
  }

  calcularAliquotaCBS(cst: string): number | null {
    if (this.possuiAliquotaIBSCBS(cst)) {
      return 0.9; // Em 2025/2026
    }

    return null;
  }

  permiteImpostosRtc(crt: number) {
    // Ao mexer nessa condição ajustar também no backend. Procurar por referências do NotasFiscaisItens.AtualizaIBSCBS.
    return crt === 3;
  }
}

export interface CalculosICMS {
  baseCalculoICMS: number
  valorCreditoICMS: number
  modBcIcms: number
  modBcStIcms: number
  aliquotaICMSID: number | null
  aliquotaICMS: number
  valorICMS: number
  reducaoBaseCalculoICMS: number
}

export interface CalculosICMS2 {
  baseCalculoICMS: number
  valorCreditoICMS: number
  modBcIcms: number
  modBcStIcms: number
  aliquotaICMS: number
  valorICMS: number
  reducaoBaseCalculoICMS: number
}

export interface CalculosICMSST {
  baseCalculoICMSSTRetido: number
  aliquotaICMSSTRetido: number
  valorICMSSTRetido: number
  valorICMSSubstituto: number
  mva: number
  baseCalculoICMSST: number
  aliquotaICMSST: number
  valorICMSST: number
  redutorMVA: number
}

export interface CalculosIPI {
  valorIPI: number
  baseCalculoIPI: number
}

export interface CalculosPIS {
  valorPIS: number
  baseCalculoPIS: number
}

export interface CalculosCOFINS {
  valorCOFINS: number
  baseCalculoCOFINS: number
}
